// Business-type presets: how a job's costs are labelled and split, and whether missing labor makes a job
// "incomplete". Pure functions: no screen code, no database. Money is handled in whole cents (src/money.js).
//
// THE ONE RULE THAT NEVER CHANGES: margin = (revenue - ALL costs) / revenue. A preset only changes
//   1. what the cost columns are called and how they are split (labels), and
//   2. whether "no labor recorded" counts toward a job being incomplete.
// The profit and margin figures are identical under every preset (see businessPreset.test.js).
//
// The three presets:
//   trades  "Trades"               cost columns Materials + Labor. A job with no labor is incomplete (the original rule).
//   team    "Services with a team" cost columns Contractors (the labor amount) + Expenses (everything else).
//                                  No labor makes a job incomplete ONLY if the owner says they pay people per job.
//   solo    "Solo services"        one Costs column (all costs). No labor never makes a job incomplete.
//
// Which preset a business gets: the owner's saved choice (contractors.cost_preset) if there is one, otherwise the
// preset that matches the industry picked at sign-up (contractors.industry), otherwise Trades.

import { toWholeCents } from "./money";

// The industries offered at sign-up (src/screens/OnboardingGate.js shows this list in this order).
export const INDUSTRY_OPTIONS = [
  // Professional Services
  "Consulting",
  "Legal Services",
  "Accounting & Bookkeeping",
  "Marketing & Advertising",
  "IT & Technology Services",
  "Photography & Video",
  "Design & Creative",
  // Trades & Construction
  "General Contracting",
  "Electrical",
  "Plumbing",
  "HVAC",
  "Landscaping",
  "Painting",
  "Flooring",
  "Roofing",
  "Renovation / Remodeling",
  // Other SMB
  "Retail",
  "Restaurant / Food Service",
  "Healthcare & Wellness",
  "Real Estate",
  "Event Services",
  "Cleaning Services",
  "Transportation & Logistics",
  "Manufacturing",
  "Other",
];

export const PRESET_KEYS = ["trades", "team", "solo"];
export const DEFAULT_PRESET = "trades";

// Industry -> preset. Anything not listed (including "skipped", "Other", empty, null, or a value typed in
// by an older version of the sign-up form) uses DEFAULT_PRESET (Trades).
export const INDUSTRY_PRESET = {
  "Consulting": "solo",
  "Legal Services": "solo",
  "Accounting & Bookkeeping": "solo",
  "Healthcare & Wellness": "solo",
  "Real Estate": "solo",
  "Marketing & Advertising": "team",
  "IT & Technology Services": "team",
  "Photography & Video": "team",
  "Design & Creative": "team",
  "Event Services": "team",
  "General Contracting": "trades",
  "Electrical": "trades",
  "Plumbing": "trades",
  "HVAC": "trades",
  "Landscaping": "trades",
  "Painting": "trades",
  "Flooring": "trades",
  "Roofing": "trades",
  "Renovation / Remodeling": "trades",
  "Retail": "trades",
  "Restaurant / Food Service": "trades",
  "Cleaning Services": "trades",
  "Transportation & Logistics": "trades",
  "Manufacturing": "trades",
  "Other": "trades",
};

// Owner-facing text for the setting screen.
export const PRESETS = {
  trades: {
    key: "trades",
    name: "Trades",
    description: "Contractors, landscapers, cleaners and others who buy materials and pay for labor. Costs show as Materials and Labor, and a job with no labor is marked incomplete.",
  },
  team: {
    key: "team",
    name: "Services with a team",
    description: "Agencies, event planners, IT teams, photographers and designers. Costs show as Contractors and Expenses.",
  },
  solo: {
    key: "solo",
    name: "Solo services",
    description: "Consultants, lawyers, accountants and others who work mostly on their own. Costs show as one Costs column.",
  },
};

export const PAYS_PEOPLE_LABEL = "I pay people per job (crew, freelancers, assistants)";

export function isPresetKey(k) {
  return PRESET_KEYS.includes(k);
}

// The preset that matches an industry. Unknown or missing industries get Trades.
export function presetForIndustry(industry) {
  return Object.prototype.hasOwnProperty.call(INDUSTRY_PRESET, industry) ? INDUSTRY_PRESET[industry] : DEFAULT_PRESET;
}

// Does a job with no labor recorded count as incomplete? Trades: always. Team: only if the owner pays people
// per job. Solo: never.
export function missingLaborCounts(key, paysPeoplePerJob) {
  if (key === "solo") return false;
  if (key === "team") return paysPeoplePerJob === true;
  return true;
}

// Does a job with revenue and no costs at all have its margin withheld ("Incomplete")? Trades and Services with a
// team: yes. Solo services: no. A solo consultant often truly has no job costs, so the real margin is shown and the
// chip says "No costs recorded" so a forgotten cost can still be spotted.
export function noCostsWithholds(key) {
  return key !== "solo";
}

// The words for "no labor" in each preset. Services with a team talk about contractor costs, not labor.
//   noun          for sentences: "... or labor", "... or contractor costs"
//   noneRecorded  the reason and chip when the job has none
//   missing       the hint under a withheld margin
function laborWordsFor(key) {
  if (key === "team") return { noun: "contractor costs", noneRecorded: "No contractor costs recorded", missing: "Contractor costs are missing" };
  return { noun: "labor", noneRecorded: "No labor recorded", missing: "Labor is missing" };
}

// Cost columns for each preset. "other" is the non-labor amount (job.materialCost), "labor" is job.laborCost.
// short is for tight table headers; tip is the hover text.
function columnsFor(key) {
  if (key === "team") {
    return [
      { id: "labor", label: "Contractors", short: "Contractors", tip: "Contractor and freelancer costs for this job" },
      { id: "other", label: "Expenses", short: "Expenses", tip: "Other costs for this job" },
    ];
  }
  if (key === "solo") return [];
  return [
    { id: "other", label: "Materials", short: "Mat'l", tip: "Material costs for this job" },
    { id: "labor", label: "Labor", short: "Labor", tip: "Labor costs for this job" },
  ];
}

// Everything a screen needs, from the profile row. profile may be null, or lack the new columns (before the
// migration is applied); both fall back to the industry mapping.
//   key               "trades" | "team" | "solo"
//   source            "setting" if the owner chose it, else "industry"
//   paysPeople        the yes/no answer (only meaningful for "team")
//   missingLaborCounts  does no labor make a job incomplete
//   laborWords          { noun, noneRecorded, missing }: the preset's words for missing labor
//   noCostsWithholds    does a job with no costs at all have its margin withheld (not for Solo services)
//   columns           the split cost columns ([] for solo)
//   totalLabel        header for the all-costs column: "Total" or "Costs"
export function resolveBusinessType(profile) {
  const p = profile || {};
  const chosen = isPresetKey(p.cost_preset) ? p.cost_preset : null;
  const key = chosen || presetForIndustry(p.industry);
  const paysPeople = p.pays_people_per_job === true;
  return {
    key,
    name: PRESETS[key].name,
    source: chosen ? "setting" : "industry",
    paysPeople,
    missingLaborCounts: missingLaborCounts(key, paysPeople),
    noCostsWithholds: noCostsWithholds(key),
    laborWords: laborWordsFor(key),
    columns: columnsFor(key),
    totalLabel: key === "solo" ? "Costs" : "Total",
  };
}

// The Trades business type, used when no screen has been given one (demo, tests, signed-out views).
export const DEFAULT_BUSINESS_TYPE = resolveBusinessType(null);

// The rule passed to jobCompleteness(job, pending, rule).
export function completenessRule(businessType) {
  const bt = businessType || DEFAULT_BUSINESS_TYPE;
  return { missingLaborCounts: bt.missingLaborCounts !== false, noCostsWithholds: bt.noCostsWithholds !== false, noLaborText: bt.laborWords.noneRecorded };
}

// One job's costs split for display, in whole cents. Total is always the job's total costs, so the parts add up
// only when the job's material + labor equal its costs (the app already keeps them equal except under a date
// filter; the Total column is the figure margin uses).
// Returns { parts: [{ id, label, cents }], totalCents }. Solo has no parts.
export function splitCosts(job, businessType) {
  const bt = businessType || DEFAULT_BUSINESS_TYPE;
  const j = job || {};
  const amount = { labor: toWholeCents(Number(j.laborCost) || 0), other: toWholeCents(Number(j.materialCost) || 0) };
  return {
    parts: bt.columns.map((c) => ({ id: c.id, label: c.label, cents: amount[c.id] })),
    totalCents: toWholeCents(Number(j.costs) || 0),
  };
}

// The small line under the job-costs total, e.g. "$4,000 material · $1,200 labor". format turns dollars into text.
export function costSummaryLine(businessType, materialDollars, laborDollars, format) {
  const bt = businessType || DEFAULT_BUSINESS_TYPE;
  const f = format || ((n) => String(n));
  if (bt.key === "solo") return "All costs on your jobs";
  if (bt.key === "team") return `${f(laborDollars)} contractors · ${f(materialDollars)} expenses`;
  return `${f(materialDollars)} material · ${f(laborDollars)} labor`;
}

// The columns to save on the contractors row for an owner's choice. Only the team preset uses the yes/no answer,
// so the others save null for it.
export function settingsRow(key, paysPeople) {
  if (!isPresetKey(key)) throw new Error("Unknown business type");
  return { cost_preset: key, pays_people_per_job: key === "team" ? paysPeople === true : null };
}
